import { useSyncExternalStore } from "react";
import { ApiError, OfflineError } from "./api.ts";
import { reportError } from "./report.ts";

/**
 * Short messages at the top of the screen.
 *
 * Every write that can fail says so here. A button that silently does nothing
 * mid-session is the failure this exists to prevent.
 */

export interface Notice {
  id: number;
  tone: "error" | "info";
  message: string;
}

const DISMISS_AFTER_MS = 6000;

let notices: Notice[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(next: Notice[]) {
  notices = next;
  listeners.forEach((listener) => listener());
}

export function dismiss(id: number): void {
  emit(notices.filter((notice) => notice.id !== id));
}

export function notify(message: string, tone: Notice["tone"] = "info"): void {
  const id = nextId++;
  // The same message twice in a row is one notice, not a stack.
  emit([...notices.filter((notice) => notice.message !== message), { id, tone, message }]);
  setTimeout(() => dismiss(id), DISMISS_AFTER_MS);
}

/** What went wrong, in words for the athlete rather than the developer. */
export function describeError(error: unknown): string {
  if (error instanceof OfflineError) return "serveur injoignable";
  if (error instanceof ApiError) return error.message || `erreur ${error.status}`;
  if (error instanceof Error) return error.message;
  return "erreur inattendue";
}

/** Tell the athlete, and log it on the server. */
export function notifyError(action: string, error: unknown): void {
  notify(`${action} : ${describeError(error)}`, "error");
  reportError(error, { action });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useNotices(): Notice[] {
  return useSyncExternalStore(subscribe, () => notices);
}
